/** The closed state of a period, the Admin's way back, and the Admin's way through — **ADR-0094**.
 *
 * Three pieces, each used where the work already happens:
 *
 * - `PeriodLockState` — on the close panel: is it closed, since when, by whom, what has happened
 *   since, and (for an Admin) the reopen.
 * - `PostCloseRetry` — under a write that was refused with `period_closed`: the served sentence,
 *   and an Admin's chance to say why and record it as a post-close change.
 * - `isPeriodClosed` (in `api/refusal.ts`) — so a screen can hand `WriteFailed` everything else.
 *
 * **Who sees which control (design rule 6).** A role that cannot reopen is not shown a reopen
 * button that would 403; it is told who can. The same for the retry: only an Admin is offered a
 * reason box, and a non-Admin who already hit the wall is told to ask one.
 *
 * **What is derived and what is served.** "Closed", the counts and *amended since issue* are all
 * the server's (`PeriodLockOut`). Nothing here ticks a box by itself and nothing infers that a
 * figure differs: *amended since issue* is a fact about the inventory, and whether the numbers
 * moved is the filing's own `matches_today`.
 */

import { useState } from "react";

import { useMe } from "../api/auth";
import {
  acknowledgesIssued,
  ISSUED_PHRASE,
  MAX_REASON_CHARS,
  reopenRefusalKind,
  usePeriodLock,
  useReopenPeriod,
  useReopenPreview,
} from "../api/periodLock";
import type { PeriodLock, ReopenResult } from "../api/periodLock";
import { useMembers } from "../api/queries";
import { useReader } from "../api/profile";
import { isPeriodClosed } from "../api/refusal";
import { ReadFailed, WriteFailed } from "../components/Loaded";
import { instantStamp, shortDate } from "../lib/format";
import styles from "./PeriodLock.module.css";

/** A reason box with its counter. Controlled by the caller so a failed attempt keeps the words. */
function ReasonField({
  id,
  label,
  value,
  onChange,
  invalid,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
  invalid?: boolean;
}) {
  return (
    <div className={styles.field}>
      <label htmlFor={id}>{label}</label>
      <textarea
        id={id}
        data-testid={id}
        value={value}
        maxLength={MAX_REASON_CHARS}
        rows={3}
        aria-invalid={invalid ? true : undefined}
        aria-describedby={`${id}-count`}
        onChange={(event) => onChange(event.target.value)}
      />
      <span id={`${id}-count`} className={styles.counter} data-testid={`${id}-count`}>
        {value.length} of {MAX_REASON_CHARS} characters
      </span>
    </div>
  );
}

/** Under a write that was refused with `period_closed`.
 *
 * Renders the served message and remedy (written once, server-side), names every period the write
 * reached, and offers an Admin the act: a reason, and the same request sent again carrying it. The
 * period stays closed — that is what separates this from a reopen. A non-Admin is told to ask one;
 * so is an Admin whose own attempt was refused as `needs_admin`, which cannot happen to an Admin
 * but is still said rather than assumed. */
export function PostCloseRetry({
  error,
  onRecord,
  pending,
}: {
  error: unknown;
  /** Send the refused request again, carrying this reason. The caller owns the request. */
  onRecord?: (reason: string) => void;
  pending?: boolean;
}) {
  const { data: me } = useMe();
  const [reason, setReason] = useState("");
  if (!isPeriodClosed(error)) return null;

  const isAdmin = me?.role === "admin";
  // No `onRecord` means the request already carries a reason the person had to give (a nil, a
  // retirement): the server counts it as an Admin's act, so an Admin is never refused here and a
  // second reason box would ask for the same words twice. Everyone else is told whose act it is.
  const mayRecord = isAdmin && !error.needsAdmin && onRecord !== undefined;
  const ready = reason.trim().length > 0 && !pending;

  return (
    <div
      className={styles.refusal}
      role="alert"
      data-testid="period-closed-refusal"
      data-code="period_closed"
    >
      <strong className={styles.refusalHead}>
        This reaches {error.periods.length === 1 ? "a closed period" : "closed periods"}.
      </strong>
      <span>{error.message}</span>
      {error.periods.length > 0 ? (
        <ul className={styles.periods} data-testid="period-closed-periods">
          {error.periods.map((p) => (
            <li key={p.periodId || p.label}>
              {p.regimeKey.replace(/_/g, " ")} {p.label}
              {p.periodStart && p.periodEnd
                ? ` (${shortDate(p.periodStart)} to ${shortDate(p.periodEnd)})`
                : ""}
              {p.closedAt ? `, closed ${shortDate(p.closedAt)}` : ""}
            </li>
          ))}
        </ul>
      ) : null}
      {mayRecord ? (
        <div className={styles.act}>
          <ReasonField
            id="post-close-reason"
            label="Why is this change needed? (kept with the change)"
            value={reason}
            onChange={setReason}
          />
          <p className={styles.hint}>
            The period stays closed. The change is recorded as a post-close change with your reason
            and the date.
          </p>
          <button
            type="button"
            className={styles.secondary}
            disabled={!ready}
            data-testid="post-close-record"
            onClick={() => onRecord?.(reason.trim())}
          >
            {pending ? "Recording…" : "Record as a post-close change"}
          </button>
        </div>
      ) : (
        <p className={styles.hint} data-testid="post-close-ask-admin">
          An <strong>Admin</strong> records a change in a closed period, or reopens it, with a
          reason.
        </p>
      )}
    </div>
  );
}

/** What the reopen was refused for, one sentence per code, each with its own remedy. */
function ReopenRefusal({
  error,
  onReload,
}: {
  error: unknown;
  onReload: () => void;
}) {
  const kind = reopenRefusalKind(error);
  const message = error instanceof Error ? error.message : "";
  if (kind === "other") {
    return <WriteFailed error={error} of="this period" onReload={onReload} />;
  }
  const head: Record<Exclude<typeof kind, "other">, string> = {
    stale_closure: "This period changed while you were looking at it.",
    not_closed: "This period is not closed.",
    already_reopened: "This period was already reopened.",
    issued_acknowledgement_required: "A filing was issued for this period.",
    invalid_post_close_reason: "That reason cannot be used.",
    forbidden: "Only an Admin can reopen a period.",
  };
  return (
    <p className={styles.refusal} role="alert" data-testid="reopen-refusal" data-code={kind}>
      <strong className={styles.refusalHead}>{head[kind]}</strong>
      {kind === "forbidden" ? null : message}
      {kind === "stale_closure" || kind === "not_closed" || kind === "already_reopened" ? (
        <button type="button" className={styles.secondary} data-testid="reopen-reload" onClick={onReload}>
          Reload
        </button>
      ) : null}
    </p>
  );
}

function eventLabel(kind: string): string {
  switch (kind) {
    case "closed":
      return "Closed";
    case "reopened":
      return "Reopened";
    case "post_close_change":
      return "Post-close change";
    default:
      return kind.replace(/_/g, " ");
  }
}

function History({ lock, nameOf }: { lock: PeriodLock; nameOf: (id: string) => string | null }) {
  const reader = useReader();
  if (lock.events.length === 0) return null;
  return (
    <details className={styles.history} data-testid="lock-history">
      <summary>History ({lock.events.length})</summary>
      <ol>
        {lock.events.map((e) => (
          <li key={e.seq}>
            <strong>{eventLabel(e.kind)}</strong> · {instantStamp(e.at, reader)}
            {nameOf(e.actor_id) ? ` · ${nameOf(e.actor_id)}` : ""}
            {e.reason ? <span className={styles.eventReason}>{e.reason}</span> : null}
          </li>
        ))}
      </ol>
    </details>
  );
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** The lock for one period, with the reopen for an Admin.
 *
 * `onReload` re-reads whatever else the host panel shows (the reconciliation), so a "Reload" after
 * a stale refusal brings the whole panel to where the server is, not only this half. */
export function PeriodLockState({
  periodId,
  onReload,
}: {
  periodId: string | undefined;
  onReload?: () => void;
}) {
  const lockRead = usePeriodLock(periodId);
  const { data: me } = useMe();
  const reader = useReader();
  const isAdmin = me?.role === "admin";
  // Names only where the roster is readable (Admin-only on the server). Elsewhere the id is not
  // printed: an unexplained UUID is worse than saying nothing about who.
  const members = useMembers(isAdmin);
  const nameOf = (id: string | null): string | null =>
    (id && members.data?.find((m) => m.user_id === id)?.display_name) || null;

  const reopen = useReopenPeriod(periodId);
  // What a reopen would do, asked for only where the reopen form is shown (an Admin, on a closed
  // period): any other reader has no use for the count and the server need not compute it.
  const preview = useReopenPreview(periodId, isAdmin && lockRead.data?.status === "locked");
  const [reason, setReason] = useState("");
  const [typed, setTyped] = useState("");
  const [refusal, setRefusal] = useState<unknown>(null);
  const [done, setDone] = useState<ReopenResult | null>(null);

  if (!periodId) return null;

  if (lockRead.error) {
    return (
      <ReadFailed
        of="This period's lock"
        error={lockRead.error}
        onRetry={() => void lockRead.refetch()}
      />
    );
  }
  const lock = lockRead.data;
  if (!lock) {
    return (
      <p className={styles.hint} role="status" aria-busy="true">
        Reading this period&rsquo;s lock…
      </p>
    );
  }

  const closed = lock.status === "locked";
  const needsAck = lock.issued_artifacts > 0;
  const ackOk = !needsAck || acknowledgesIssued(typed);
  const canSubmit = closed && reason.trim().length > 0 && ackOk && !reopen.isPending;
  const closedBy = nameOf(lock.closed_by);
  const seen = lock.closure_seq;

  function reload() {
    setRefusal(null);
    void lockRead.refetch();
    onReload?.();
  }

  function submit() {
    setRefusal(null);
    setDone(null);
    reopen.mutate(
      {
        reason: reason.trim(),
        expectedClosureSeq: seen,
        // Sent only when typed. The server refuses a reopen over an issued filing without it.
        acknowledgeIssued: needsAck && acknowledgesIssued(typed),
      },
      {
        onSuccess: (result) => {
          setDone(result);
          setReason("");
          setTyped("");
        },
        onError: (error) => {
          setRefusal(error);
          // The refusal may mean our copy of the lock is the wrong one (a filing issued since, or
          // someone else's reopen), so the form is rebuilt from the server's.
          void lockRead.refetch();
        },
      },
    );
  }

  return (
    <div className={styles.state} data-testid="period-lock" data-status={lock.status}>
      {closed ? (
        <p className={styles.closed} data-testid="period-closed">
          <strong>Closed</strong>
          {lock.closed_at ? ` on ${instantStamp(lock.closed_at, reader)}` : ""}
          {closedBy ? ` by ${closedBy}` : ""}. Changes now need an Admin and a reason.
        </p>
      ) : (
        <p className={styles.hint} data-testid="period-open">
          {lock.reopened_count > 0
            ? `Open again: reopened ${plural(lock.reopened_count, "time", "times")} since it was first closed. It has to be closed again to be finished.`
            : "Not closed."}
        </p>
      )}

      {lock.post_close_changes > 0 || lock.reopened_count > 0 ? (
        <p className={styles.hint} data-testid="lock-counts">
          {plural(lock.post_close_changes, "post-close change", "post-close changes")} ·{" "}
          {plural(lock.reopened_count, "reopen", "reopens")}
        </p>
      ) : null}

      {/* Derived by the server, never ticked here. It says the inventory changed after a filing
          was issued; it does not say the figures differ — the filing's own comparison does. */}
      {lock.amended_since_issue ? (
        <p className={styles.flag} data-testid="amended-since-issue">
          <strong>Amended since issue.</strong> This period was reopened or changed after a filing
          was issued. The issued filing is unchanged; whether today&rsquo;s figures differ from it
          is shown on the filing.
        </p>
      ) : null}

      <History lock={lock} nameOf={nameOf} />

      {done ? (
        <p className={styles.done} role="status" data-testid="reopen-done">
          {done.already_reopened
            ? "This period was already reopened with that reason. Nothing was changed."
            : `Reopened. ${plural(done.invalidated_approvals, "filing approval was", "filing approvals were")} withdrawn. Nothing already issued was changed or reissued.`}
        </p>
      ) : null}

      {closed && isAdmin ? (
        <div className={styles.form} data-testid="reopen-form">
          <ReasonField
            id="reopen-reason"
            label="Reason for reopening"
            value={reason}
            onChange={setReason}
            invalid={reopenRefusalKind(refusal) === "invalid_post_close_reason"}
          />
          {needsAck ? (
            <div className={styles.field}>
              <label htmlFor="reopen-ack">
                {plural(lock.issued_artifacts, "filing", "filings")} already issued for this period
                will stay exactly as filed, and the period will read as amended since issue. Type{" "}
                <strong>{ISSUED_PHRASE}</strong> to go on.
              </label>
              <input
                id="reopen-ack"
                data-testid="reopen-ack"
                type="text"
                value={typed}
                autoComplete="off"
                onChange={(event) => setTyped(event.target.value)}
              />
            </div>
          ) : null}
          {/* **What this will do, from the server's own count** (B14): the number of filing
              approvals it would withdraw is read by the statement the reopen then runs, so this
              sentence and the result shown after the act are two readings of one thing. A read that
              failed says so rather than saying nothing would be withdrawn. */}
          {preview.error ? (
            <ReadFailed
              of="What a reopen would do"
              error={preview.error}
              onRetry={() => void preview.refetch()}
            />
          ) : (
            <p className={styles.hint} data-testid="reopen-effect">
              {preview.data ? preview.data.sentence : "Reading what a reopen would do…"}
            </p>
          )}
          <button
            type="button"
            className={styles.secondary}
            disabled={!canSubmit}
            data-testid="reopen-submit"
            onClick={submit}
          >
            {reopen.isPending ? "Reopening…" : "Reopen this period"}
          </button>
          <ReopenRefusal error={refusal} onReload={reload} />
        </div>
      ) : null}

      {closed && me && !isAdmin ? (
        <p className={styles.hint} data-testid="reopen-who-can">
          An <strong>Admin</strong> reopens a closed period, with a reason.
        </p>
      ) : null}
    </div>
  );
}
